/**
 * clusterPins — merge pins that would overlap on screen into one group.
 *
 * Clustering is done in SCREEN pixels at the zoom the pins were projected at, not by fixed
 * regions: two cameras a street apart are one blob over the whole state and two pins over the
 * city, and the map never has to know where a district boundary runs. Zoomed out on a state
 * this falls out as roughly one group per town or district, because that is where cameras
 * sit together.
 *
 * Greedy and deterministic: points are visited in id order, each unclaimed point seeds a group
 * and claims every unclaimed point within `radius` of it. The same cameras at the same zoom
 * always give the same groups, so a settle that changes nothing does not reshuffle the map.
 * A grid of `radius`-sized cells keeps it linear — only the 3×3 cells around a seed are
 * searched.
 */

export interface PinPoint<T> {
  id: string;
  x: number;
  y: number;
  lat: number;
  lng: number;
  item: T;
}

export interface PinGroup<T> {
  /** Stable React key — the seed's id. */
  key: string;
  /** Where to draw the group: the centre of its members, in the same pixels as the input. */
  x: number;
  y: number;
  members: PinPoint<T>[];
}

export function clusterPins<T>(points: PinPoint<T>[], radius: number): PinGroup<T>[] {
  const sorted = [...points].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const cell = (v: number) => Math.floor(v / radius);
  const grid = new Map<string, PinPoint<T>[]>();
  for (const p of sorted) {
    const k = `${cell(p.x)}:${cell(p.y)}`;
    const bucket = grid.get(k);
    if (bucket) bucket.push(p);
    else grid.set(k, [p]);
  }

  const claimed = new Set<string>();
  const r2 = radius * radius;
  const groups: PinGroup<T>[] = [];
  for (const seed of sorted) {
    if (claimed.has(seed.id)) continue;
    claimed.add(seed.id);
    const members = [seed];
    const cx = cell(seed.x);
    const cy = cell(seed.y);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const p of grid.get(`${cx + dx}:${cy + dy}`) ?? []) {
          if (claimed.has(p.id)) continue;
          const ex = p.x - seed.x;
          const ey = p.y - seed.y;
          if (ex * ex + ey * ey <= r2) {
            claimed.add(p.id);
            members.push(p);
          }
        }
      }
    }
    const x = members.reduce((s, m) => s + m.x, 0) / members.length;
    const y = members.reduce((s, m) => s + m.y, 0) / members.length;
    groups.push({ key: seed.id, x, y, members });
  }
  return groups;
}

/** Ground size of a group's footprint in metres (the diagonal of its lat/lng box). */
export function groupSpanMetres(members: Array<{ lat: number; lng: number }>): number {
  let s = Infinity, n = -Infinity, w = Infinity, e = -Infinity;
  for (const m of members) {
    s = Math.min(s, m.lat);
    n = Math.max(n, m.lat);
    w = Math.min(w, m.lng);
    e = Math.max(e, m.lng);
  }
  const midLat = ((s + n) / 2) * (Math.PI / 180);
  const dy = (n - s) * 111_320;
  const dx = (e - w) * 111_320 * Math.cos(midLat);
  return Math.hypot(dx, dy);
}
